/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B080050 店別出荷数量訂正
 */

import { Column, Query } from "@material-table/core";
import { Box } from "@mui/material";
import {
  B000111Get,
  B000150TokuiNmr,
  B000151NonyuNm,
  B000200ByJanCdShohinNmr,
  B000200ShohinNm,
  B000223BatchNm,
  B080050Find,
  B080050FindRequest,
  B080050Update,
  B080050UpdateField,
  B080050UpdateRequest,
  catchRestError,
} from "clients/rest";
import {
  msgState,
  SConfirmDialog,
  SDateField,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberCell,
  SNumberField,
  STable,
  STextCell,
  STextField,
} from "components";
import { getClientError, getClientMessage } from "messages";
import { getSCode, HyoziShukajokyo } from "messages/codes";
import { labels } from "messages/labels";
import { B000111, B000150, B000151, B000200, B000223 } from "pages";
import React, { createRef, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { date, mapIds } from "services";
import { sutiShukajokyo } from "services/codes";
import { focusState, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  shukashijiYmd: string;
  shijikaisu: string;
  batchNo: string;
  batchNm: string;
  tokuiCd: string;
  tokuishitenCd: string;
  tokuiNmr: string;
  nonyuCd: string;
  nonyushitenCd: string;
  nonyuNm: string;
  tenpoNmr: string;
  shohinCd: string;
  shohinNm: string;
  janCd: string;
  shohinNmr: string;
}>;

// テーブルデータ定義
type Data = Partial<{
  gyoNo: number;
  locationCd: string;
  tokuiCdNonyuCd: string;
  tokuiNmr: string;
  shohinCdJanCd: string;
  shohinNm: string;
  juchudenpyoNo: string;
  juchudenpyoNoF: string;
  shukayoteisu: string;
  shukakakuteisu: string;
  keppinriyuCd: string;
  keppinriyuNm: string;
  shukajokyo: number;
  shukajokyoView: string;
  juchuYmd: string;
  juchugyoNo: string;
  juchukoseigyoNo: string;
  version: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  shukashijiYmd: date(), // 当日日付
  shijikaisu: "",
  batchNo: "",
  batchNm: "",
  tokuiCd: "",
  tokuishitenCd: "",
  tokuiNmr: "",
  nonyuCd: "",
  nonyushitenCd: "",
  nonyuNm: "",
  tenpoNmr: "",
  shohinCd: "",
  shohinNm: "",
  janCd: "",
  shohinNmr: "",
};

// フォーカスIDの設定
const focusId = "B080050";

export const B080050 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef<any>();
  // 初回検索用のフラグ
  const searched = useRef(false);
  // 検索ボタン押下時に初期ページにするステート
  const isDefaultPage = useRef(false);
  // 編集中の更新フラグ
  const editing = useRef(false);
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();
  // 訂正ボタン活性制御
  const [disabledCorrect, setDisabledCorrect] = useState(true);
  // 編集データ
  const rows = useRef<Data[]>([]);
  // セル値取得
  const cellValue = (gyoNo: number | undefined, name: string) => {
    const value: any = rows.current.filter((v) => v.gyoNo === gyoNo).pop();
    if (value && name in value) return value[name];
    return undefined;
  };
  // 編集行更新
  const updateRows = () => {
    editing.current = true;
    searched.current = true;
    tableRef.current.onQueryChange();
  };
  // カラム編集
  const editCell = (gyoNo: number | undefined, name: string, value: any) => {
    rows.current = rows.current.map((v: Data) => {
      if (v.gyoNo === gyoNo) (v as any)[name] = value;
      return v;
    });
  };
  // ページサイズ
  const pageSize = 15;
  // テーブルを定義する。
  const columns: Column<Data>[] = [
    {
      field: "locationCd",
      title: "ロケーション",
      width: "10rem",
      editable: "never",
    },
    {
      field: "tokuiCdNonyuCd",
      title: "得意先-納入先",
      width: "13rem",
      editable: "never",
    },
    {
      field: "tokuiNmr",
      title: "納入先名",
      width: "11rem",
      editable: "never",
    },
    {
      field: "shohinCdJanCd",
      title: "商品コード(JANコード)",
      width: "16rem",
      editable: "never",
    },
    { field: "shohinNm", title: "商品名", width: "15rem", editable: "never" },
    {
      field: "juchudenpyoNoF",
      title: "伝票番号",
      width: "7rem",
      editable: "never",
    },
    { field: "juchudenpyoNo", hidden: true },
    {
      field: "shukayoteisu",
      title: "出荷予定数　",
      width: "7rem",
      align: "right",
      editable: "never",
    },
    {
      field: "shukakakuteisu",
      title: "出荷確定数　",
      width: "7rem",
      align: "right",
      editComponent: (props) => {
        const gyoNo = props.rowData.gyoNo;
        return (
          <SNumberCell
            editProps={props}
            value={cellValue(gyoNo, "shukakakuteisu")}
            size={4}
            align="right"
            onBlur={(event) => {
              const value = event.currentTarget.value;
              editCell(gyoNo, "shukakakuteisu", value);
            }}
            disabled={
              props.rowData.shukajokyo === sutiShukajokyo.kakuteizumi ||
              props.rowData.shukajokyo === sutiShukajokyo.cancel
            }
          />
        );
      },
    },
    {
      field: "keppinriyuCd",
      title: "欠品理由コード",
      width: "7rem",
      editComponent: (props) => {
        const gyoNo = props.rowData.gyoNo;
        return (
          <SNumberCell
            editProps={props}
            value={cellValue(gyoNo, "keppinriyuCd")}
            code
            size={2}
            dialog={<B000111 meishoKbn="70" />}
            onBlur={(event) => {
              const oldValue = cellValue(gyoNo, "keppinriyuCd");
              const newValue = event.currentTarget.value ?? "";
              if (oldValue === newValue) {
                setMsg("");
                return;
              }
              editCell(gyoNo, "keppinriyuCd", newValue);
              if (newValue === "") {
                setMsg("");
                editCell(gyoNo, "keppinriyuNm", "");
                updateRows();
                return;
              }
              B000111Get({ meishoCd: newValue, meishoKbn: "70" })
                .then((response) => {
                  setMsg("");
                  editCell(gyoNo, "keppinriyuNm", response.data.meishoNm1);
                })
                .catch((reason) => {
                  editCell(gyoNo, "keppinriyuNm", "");
                  catchRestError(reason, navigate, setMsg);
                })
                .finally(() => {
                  updateRows();
                });
            }}
            handleSubmit={(data: any) => {
              editCell(gyoNo, "keppinriyuCd", data.meishoCd);
              editCell(gyoNo, "keppinriyuNm", data.meishoNm1);
              setMsg("");
              updateRows();
            }}
            disabled={
              props.rowData.shukajokyo === sutiShukajokyo.kakuteizumi ||
              props.rowData.shukajokyo === sutiShukajokyo.cancel
            }
          />
        );
      },
    },
    {
      field: "keppinriyuNm",
      title: "欠品理由名称",
      width: "7rem",
      editComponent: (props) => {
        const gyoNo = props.rowData.gyoNo;
        return (
          <STextCell
            editProps={props}
            value={cellValue(gyoNo, "keppinriyuNm")}
            disabled
          />
        );
      },
    },
    {
      field: "shukajokyoView",
      title: "出荷状況　",
      width: "6rem",
      render: (data) => {
        let color = "";

        // 出荷状況 指示済 青
        if (data.shukajokyo === sutiShukajokyo.shijizumi) {
          color = "#0000FF";
          return (
            <Box sx={{ color: color }}>
              {
                getSCode(HyoziShukajokyo, data.shukajokyo.toString() ?? "1")
                  ?.label
              }
            </Box>
          );

          // 出荷状況 確定済 緑
        } else if (data.shukajokyo === sutiShukajokyo.kakuteizumi) {
          color = "#008000";
          return (
            <Box sx={{ color: color }}>
              {
                getSCode(HyoziShukajokyo, data.shukajokyo.toString() ?? "2")
                  ?.label
              }
            </Box>
          );

          // 出荷状況 キャンセル 黒
        } else if (data.shukajokyo === sutiShukajokyo.cancel) {
          color = "#000000";
          return (
            <Box sx={{ color: color }}>
              {
                getSCode(HyoziShukajokyo, data.shukajokyo.toString() ?? "9")
                  ?.label
              }
            </Box>
          );
        }
      },
      editable: "never",
    },
    { field: "shukajokyo", hidden: true, editable: "never" },
    { field: "juchuYmd", hidden: true, editable: "never" },
    { field: "juchugyoNo", hidden: true, editable: "never" },
    { field: "juchukoseigyoNo", hidden: true, editable: "never" },
    { field: "version", hidden: true },
    { field: "gyoNo", hidden: true },
  ];

  // 初期表示イベント
  useEffect(() => {
    // 初回フォーカス先
    focus(1);
  }, []);

  // 検索時の初期化処理
  const handleResetSearch = () => {
    // 修正データをリセットする
    tableRef.current.dataManager.clearBulkEditChangedRows();
    (tableRef.current as any).handleClearSelect();
  };

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 初期化
    handleResetSearch();
    // 検索可能に変更
    searched.current = true;
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    tableRef.current.onQueryChange();
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched.current) {
        isDefaultPage.current = false;
        searched.current = false;
        editing.current = false;
        setMsg("");
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }

      // 編集内容の一時更新
      if (editing.current) {
        editing.current = false;
        resolve({
          data: rows.current,
          page: query.page,
          totalCount: query.totalCount,
        });
        return;
      }

      // 修正データをリセットする
      tableRef?.current.dataManager.clearBulkEditChangedRows();
      rows.current = [];

      // 検索フォームのデータを取得する。
      const fields = methods.getValues();

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータを作成する。
      const param: B080050FindRequest = {
        shukashijiYmd: fields.shukashijiYmd!,
        shijikaisu: fields.shijikaisu,
        batchNo: fields.batchNo,
        tokuiCd: fields.tokuiCd,
        tokuishitenCd: fields.tokuishitenCd,
        nonyuCd: fields.nonyuCd,
        nonyushitenCd: fields.nonyushitenCd,
        shohinCd: fields.shohinCd,
        janCd: fields.janCd,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        // ["出力項目名,順番(asc/desc", ...]
        sort: ["shukashijiYmd,asc", "shijikaisu,asc", "batchNo,asc"],
      };
      // API呼び出し
      B080050Find(param)
        // 正常取得時
        .then((value) => {
          // 訂正ボタン活性化
          if (value.data.content.length === 0) {
            setDisabledCorrect(true);
            // データ非存在メッセージ表示
            setMsg("指定された検索条件に合致するデータは存在しません。");
          } else {
            setDisabledCorrect(false);
          }
          const offset = query.pageSize * query.page;
          rows.current = mapIds(value.data.content, "gyoNo", offset);
          resolve({
            data: rows.current,
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })

        // エラー時
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // 訂正ボタンクリックイベント
  const handleCorrect = () => {
    const filtered = rows.current.filter((v, i) => v.shukakakuteisu);

    // 出荷確定数の変更がない場合はエラーとする。
    if (filtered.length <= 0) {
      setMsg(getClientError("B080050_001"));
      return;
    }

    // 出荷確定数が出荷予定数を超過する場合はエラーとする。
    filtered.forEach((v) => {
      if (Number(v.shukakakuteisu) > Number(v.shukayoteisu)) {
        setMsg(getClientError("B080050_002"));
        return;
      }
    });

    // リクエスト作成
    const param: B080050UpdateRequest = {
      b080050UpdateFieldList: filtered.map((v) => {
        return {
          juchuYmd: v.juchuYmd,
          juchudenpyoNo: v.juchudenpyoNo,
          juchugyoNo: v.juchugyoNo,
          juchukoseigyoNo: v.juchukoseigyoNo,
          locationCd: v.locationCd,
          shukayoteisu: v.shukayoteisu,
          shukakakuteisu: v.shukakakuteisu,
          version: v.version,
          keppinriyuCd: v.keppinriyuCd,
          keppinriyuNm: v.keppinriyuNm,
        } as B080050UpdateField;
      }),
    };
    // 実行API呼び出し
    B080050Update(param)
      .then(() => {
        setMsg(getClientMessage("B080050_001"));
        handleClickSearchButton();
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="店別出荷数量訂正"
        gridTemplateColumns="7rem 6rem 5rem 14rem 3rem 7rem 6rem 5rem 17rem "
      >
        <SLabel required>出荷指示日</SLabel>
        <SDateField
          required
          label="出荷指示日"
          name="shukashijiYmd"
          {...register(1)}
          gridColumn="span 2"
        />

        <Box gridColumn="span 2" />

        <SLabel>指示回数</SLabel>
        <SNumberField
          name="shijikaisu"
          code
          size={4}
          {...register(2)}
          gridColumn="span 2"
        />

        <Box gridColumn="span 3" />

        <Box gridColumn="span 2" />

        <SLabel>バッチ番号</SLabel>
        <SNumberField
          name="batchNo"
          code
          size={4}
          {...register(3)}
          gridColumn="span 2"
          onBlur={() => B000223BatchNm(methods, navigate, setMsg)}
          dialog={<B000223 />}
          handleSubmit={(data: any) => {
            methods.setValue("batchNo", data.batchNo);
            methods.setValue("batchNm", data.batchNm);
          }}
        />
        <STextField name="batchNm" disabled />

        <SLabel>得意先コード</SLabel>
        <SNumberField
          name="tokuiCd"
          code
          size={6}
          {...register(4)}
          onBlur={() => B000150TokuiNmr(methods, navigate, setMsg)}
        />
        <SNumberField
          name="tokuishitenCd"
          code
          size={4}
          {...register(5)}
          dialog={<B000150 />}
          handleSubmit={(data: any) => {
            methods.setValue("tokuiCd", data.tokuiCd);
            methods.setValue("tokuishitenCd", data.tokuishitenCd);
            methods.setValue("tokuiNmr", data.tokuiNmr);
          }}
          onBlur={() => B000150TokuiNmr(methods, navigate, setMsg)}
        />
        <STextField name="tokuiNmr" disabled />

        <Box />

        <SLabel>納入先コード</SLabel>
        <SNumberField
          name="nonyuCd"
          code
          size={6}
          {...register(6)}
          onBlur={() => B000151NonyuNm(methods, navigate, setMsg)}
        />
        <SNumberField
          name="nonyushitenCd"
          code
          size={4}
          {...register(7)}
          dialog={<B000151 />}
          handleSubmit={(data: any) => {
            methods.setValue("nonyuCd", data.nonyuCd);
            methods.setValue("nonyushitenCd", data.nonyushitenCd);
            methods.setValue("nonyuNm", data.tokuiNmr);
          }}
          onBlur={() => B000151NonyuNm(methods, navigate, setMsg)}
        />
        <STextField name="nonyuNm" disabled />

        <SLabel gridColumn="1">商品コード</SLabel>
        <SNumberField
          name="shohinCd"
          code
          size={10}
          {...register(8)}
          gridColumn="span 2"
          dialog={<B000200 />}
          handleSubmit={(data: any) => {
            methods.setValue("shohinCd", data.shohinCd);
            methods.setValue("shohinNm", data.shohinNmr);
          }}
          onBlur={() => B000200ShohinNm(methods, navigate, setMsg)}
        />

        <STextField name="shohinNm" disabled />

        <Box gridColumn="span 1" />

        <SLabel>JANコード</SLabel>
        <SNumberField
          name="janCd"
          code
          size={13}
          {...register(9)}
          gridColumn="span 2"
          dialog={<B000200 />}
          handleSubmit={(data: any) => {
            methods.setValue("janCd", data.janCd);
            methods.setValue("shohinNmr", data.shohinNmr);
          }}
          onBlur={() => B000200ByJanCdShohinNmr(methods, navigate, setMsg)}
        />

        <STextField name="shohinNmr" disabled />

        <Box gridColumn="span 2" />

        <SNormalButton
          label={labels.buttons.search}
          onClick={handleClickSearchButton}
          gridRow="6"
          gridColumn="9"
          {...register(10)}
          align="right"
        />
      </SFrame>
      <SFrame>
        <STable
          keyName="gyoNo"
          data={handleSearch}
          columns={columns}
          pageSize={pageSize}
          tableRef={tableRef}
          width={1260}
          height={440}
          fixed={3}
          edit
          onPageChange={handleResetSearch}
        />
      </SFrame>

      <SFrame gridTemplateColumns="78.7rem">
        <SNormalButton
          label={labels.buttons.correct}
          align="right"
          {...register(13)}
          disabled={disabledCorrect}
          dialog={
            <SConfirmDialog
              title="出荷確定数訂正"
              msg={labels.dialog.correctConfirm}
            />
          }
          dialogAction="confirm"
          handleSubmit={handleCorrect}
        />
      </SFrame>
    </FormProvider>
  );
};
